import { Component, ViewChild, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';

import { WindowService } from 'src/shared/providers/window.service';

import { Util } from 'src/shared/util/util';

import { PropertyReceivablesService, PropertyReceivable } from 'src/shared/services/property-receivables.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'receivables-content',
  templateUrl: './receivables-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReceivablesContentComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private propertyReceivablesService = inject(PropertyReceivablesService);
  private util = inject(Util);

  readonly windowService = inject(WindowService);

  receivables: PropertyReceivable[] = [];
  headers: string[] = [];
  rows: any[] = [];
  isDataLoaded = false;
  errorMessage: string;
  @ViewChild('tidyTable') tidyTable: any;

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load receivables';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.buildHeaders();
    try {
      this.receivables = await this.propertyReceivablesService.getPropertyReceivables();
    } catch {
      this.receivables = [];
    }
    this.buildRows(this.receivables);
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Guest',
      'Description',
      'Channel',
      'Due',
      'Paid',
      'Status',
      'last-column'
    ];
  }

  buildRows(receivables: PropertyReceivable[]): void {
    this.rows = receivables.map(receivable => ({
      id: receivable.id,
      'Property': {
        value: receivable.property_name || 'Unknown Property',
        action: null,
      },
      'Guest': {
        value: receivable.guest_name || '',
        action: null,
      },
      'Description': {
        value: receivable.description || '',
        action: null,
      },
      'Channel': {
        value: receivable.channel || '',
        action: null,
      },
      'Due': {
        value: receivable.amount_due !== null ? `$${receivable.amount_due.toLocaleString('en-US')}` : '',
        action: null,
      },
      'Paid': {
        value: receivable.amount_paid !== null ? `$${receivable.amount_paid.toLocaleString('en-US')}` : '',
        action: null,
      },
      'Status': {
        value: receivable.status || '',
        action: null,
      },
      'last-column': {
        value: receivable.id,
        action: null,
        hasChevron: true,
      },
    }));
  }

  sortChanged(sort: any): void {
    this.rows.sort((a, b) => {
      const valA = a[sort.active]?.value || '';
      const valB = b[sort.active]?.value || '';

      if (sort.active === 'Due' || sort.active === 'Paid') {
        const numA = parseFloat(String(valA).replace(/[$,]/g, '')) || 0;
        const numB = parseFloat(String(valB).replace(/[$,]/g, '')) || 0;
        return sort.direction === 'asc' ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
      if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
      return 0;
    });
  }

  onTableColumnClick(event: any): void {
    const foundReceivable = this.receivables.find(r => r.id === event.element.id);
    if (!foundReceivable) {return;}
  }

  getEmptyStateMessage(): string {
    return 'No receivables yet. Booking payments, rental income, and fees will appear here as reservations are created.';
  }
}
